import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Badge, SearchField } from '@dreamtree-org/twreact-ui';
import { Search } from 'lucide-react';
import { PageHeader, QueryState, EmptyState, StatusBadge, Money } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatDate } from '@/lib/format';
import StatusTabs from '../components/StatusTabs';
import { ORDER_QUEUES, acceptCountdown } from '../lib/orders';
import { labelOf } from '../lib/catalog';

const TABS = [{ key: 'all', label: 'All' }, ...ORDER_QUEUES.map((k) => ({ key: k, label: labelOf(k) }))];

/** SELL-05 — order queue (SalesOrder.queue: paid orders only, filtered and searched server-side). */
export default function Orders() {
  const nav = useNavigate();
  const [tab, setTab] = useState('new');
  const [q, setQ] = useState('');
  const orders = useQuery('merchantOrders', { status: tab, q });
  const counts = (orders.data && orders.data.counts) || {};
  // The acceptance countdown ticks once a minute (MERCH-58).
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-4">
      <PageHeader title="Orders" subtitle="Accept within 24 hours, scan the exact unit, pack with evidence and dispatch with tracking." />
      <StatusTabs value={tab} onChange={setTab} tabs={TABS.map((t) => ({ ...t, count: counts[t.key] }))} />
      <div className="max-w-md">
        <SearchField
          placeholder="Order #, customer, product or SKU…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          leftIcon={<Search size={16} />}
          aria-label="Search orders" />
      </div>
      <QueryState query={orders}>
        {(res) =>
          res.data.length ? (
            <Table
              data={res.data}
              columns={[
                { key: 'id', label: 'Order', render: (o) => <span className="font-medium">#{o.id}</span> },
                { key: 'placed_at', label: 'Placed', render: (o) => formatDate(o.placed_at) },
                { key: 'customer', label: 'Customer', render: (o) => `${o.customer.name} · ${o.customer.area}` },
                { key: 'item', label: 'Product', render: (o) => o.item.title, hideOnMobile: true },
                { key: 'unit', label: 'Unit', render: (o) => <span className="font-mono text-xs">{o.unit.sku}</span>, hideOnMobile: true },
                { key: 'amount_paise', label: 'Amount', render: (o) => <Money paise={o.amount_paise} /> },
                {
                  key: 'status', label: 'Status',
                  render: (o) => (
                    <span className="inline-flex flex-wrap gap-1">
                      <StatusBadge status={o.status} />
                    </span>
                  ),
                },
                {
                  key: 'accept_by', label: 'Accept by',
                  render: (o) => {
                    const c = acceptCountdown(o, now);
                    if (!c) return o.exception_reason ? <span className="text-xs" title={o.exception_reason}>Exception</span> : <span className="opacity-60">—</span>;
                    return <Badge variant={c.overdue ? 'error' : c.urgent ? 'warning' : 'default'} size="sm">{c.label}</Badge>;
                  },
                },
              ]}
              onRowClick={(o) => nav(`/orders/${o.id}`)}
              showSerial={false}
              withAction={false}
              showColumnPicker={false}
              pagination
              pageSize={25}
              onReload={orders.reload}
            />
          ) : (
            <EmptyState title={`No ${tab === 'all' ? '' : labelOf(tab).toLowerCase()} orders`.replace('  ', ' ')}>Orders appear here as customers check out.</EmptyState>
          )
        }
      </QueryState>
    </div>
  );
}
